import { useCallback } from 'react';
import accessToken from '|/helpers/access-token';
import useAnalyticsContext from '|/hooks/use-analytics-context';

const useEventHelper = () => {
  const { trackEvent } = useAnalyticsContext();

  const sendLoadEvent = useCallback(
    (success: boolean, additionalMetadata?: object) => {
      const metadata = {
        ...additionalMetadata
      };
      trackEvent('pp_load', {
        url: window.location.pathname,
        userId: accessToken().user.user_id,
        successEvent: success,
        metadata: metadata,
        devicePlatform: 'web'
      });
    },
    [trackEvent]
  );

  const sendShowEvent = useCallback(
    (additionalMetadata?: object) => {
      trackEvent('pp_show', {
        url: window.location.pathname,
        userId: accessToken().user.user_id,
        metadata: { ...additionalMetadata },
        devicePlatform: 'web'
      });
    },
    [trackEvent]
  );

  const sendCompleteEvent = useCallback(
    (success: boolean | string, metadata?: object) => {
      const attributesToSend = {
        url: window.location.pathname,
        userId: accessToken().user.user_id,
        successEvent: success,
        metadata: metadata ?? {},
        devicePlatform: 'web'
      };
      trackEvent('pp_complete', attributesToSend);
    },
    [trackEvent]
  );

  const sendEvent = useCallback(
    (eventName: string, metadata?: object) => {
      trackEvent(eventName, {
        url: window.location.pathname,
        userId: accessToken().user.user_id,
        metadata: metadata ?? {},
        devicePlatform: 'web'
      });
    },
    [trackEvent]
  );

  const sendEventWithSession = useCallback(
    (eventName: string, metadata?: object) => {
      sendEvent(eventName, { ...metadata });
    },
    [sendEvent]
  );

  const sendClickEvent = useCallback(
    (analyticsId: string) => {
      trackEvent('pp_click', {
        analyticsId
      });
    },
    [trackEvent]
  );

  const sendRenderEvent = useCallback(
    (experimentKey: string, bucketKey: string, name = '') => {
      trackEvent('experiment-rendered', {
        experimentKey,
        bucketKey,
        name
      });
    },
    [trackEvent]
  );

  const sendSkipEvent = useCallback(
    (data: { steps: Record<string, boolean>; currentStep: string; previousStep: string }) => {
      sendEventWithSession('pp_skip', data);
    },
    [sendEventWithSession]
  );

  return {
    trackEvent,
    sendLoadEvent,
    sendShowEvent,
    sendCompleteEvent,
    sendEvent,
    sendClickEvent,
    sendSkipEvent,
    sendRenderEvent,
    sendEventWithSession
  };
};

export default useEventHelper;
